import {
  hasKeys,
  decryptWillKeyWithPrivateKey,
  decryptWillItem,
  decryptWillFileBlob,
  validateBip39Mnemonic,
  deriveWillLookupKey,
  deriveWillWrappingKey,
  unwrapWillKey,
  wipe,
} from '../crypto.js';
import { get, getRaw } from '../api.js';
import { esc } from '../app.js';
import { toast } from '../dialog.js';

export async function renderWillAccess(app, params) {
  const token = params.token;

  if (!token) {
    renderWillEntry(app);
    return;
  }

  // Check if token is a word-slug (12 hyphen-separated words)
  const slugWords = token.split('-');
  if (slugWords.length === 12 && slugWords.every(w => /^[a-z]+$/.test(w))) {
    renderWordSlugAccess(app, slugWords);
    return;
  }

  app.innerHTML = '<div class="card"><p class="loading">Loading will data...</p></div>';

  try {
    const data = await get(`/will/access/${token}`);
    let willKey = null;

    if (hasKeys() && data.ephemeral_pubkey.length > 1) {
      try {
        willKey = decryptWillKeyWithPrivateKey(data.encrypted_will_key, data.ephemeral_pubkey);
      } catch (e) {
        // Fall through
      }
    }

    if (willKey) {
      const decryptedItems = decryptAllItems(data, willKey);
      renderDecryptedView(app, decryptedItems, token, willKey);
    } else {
      renderLockedView(app, data, token);
    }
  } catch (err) {
    app.innerHTML = `
      <div class="card">
        <div class="msg msg-error">${esc(err.message)}</div>
      </div>
    `;
  }
}

async function renderWordSlugAccess(app, words) {
  const mnemonic = words.join(' ');
  app.innerHTML = `
    <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
      <h2>Digital Heir Access</h2>
      <p class="loading" id="slug-status">Verifying passphrase...</p>
      <div id="slug-msg" style="min-height:1.5em;margin-top:0.5rem"></div>
    </div>`;

  try {
    const valid = await validateBip39Mnemonic(mnemonic);
    if (!valid) throw new Error('Invalid passphrase words in URL.');

    document.getElementById('slug-status').textContent = 'Looking up will...';
    const lookupKey = await deriveWillLookupKey(mnemonic);
    const data = await get(`/will/access/by-words/${lookupKey}`);

    document.getElementById('slug-status').textContent = 'Deriving key...';
    const wrappingKey = await deriveWillWrappingKey(mnemonic, data.will_key_version || 1);
    const encryptedWillKey = new Uint8Array(data.encrypted_will_key);
    const willKey = unwrapWillKey(wrappingKey, encryptedWillKey);
    wipe(wrappingKey);

    document.getElementById('slug-status').textContent = 'Decrypting items...';
    const decryptedItems = decryptAllItems(data, willKey);
    // Use lookupKey as token for blob fetches
    renderDecryptedView(app, decryptedItems, null, willKey, lookupKey);
  } catch (err) {
    if (err.message.includes('404') || err.message.includes('Not Found')) {
      app.innerHTML = `
        <div class="card" style="text-align:center">
          <h2>Will Not Found</h2>
          <p style="color:var(--text-dim)">This will access link is invalid or has expired.</p>
        </div>`;
    } else {
      // Fall back to manual word entry with error
      renderWillEntry(app);
      const entryMsg = document.getElementById('will-msg');
      if (entryMsg) entryMsg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
      const inputs = app.querySelectorAll('.mnemonic-word');
      words.forEach((w, i) => { if (inputs[i]) inputs[i].value = w; });
    }
  }
}

function renderWillEntry(app) {
  app.innerHTML = `
    <div class="card" style="text-align:center;max-width:480px;margin:0 auto">
      <h2>Digital Heir Access</h2>
      <p style="color:var(--text-dim);margin-bottom:1rem">Enter the 12-word passphrase you received.</p>
      <div class="mnemonic-grid" style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:1rem">
        ${Array.from({length: 12}, (_, i) => `
          <input type="text" class="mnemonic-word" data-idx="${i}" placeholder="${i + 1}" autocomplete="off" autocapitalize="none" spellcheck="false"
            style="padding:8px 6px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:13px;font-family:'JetBrains Mono',monospace;text-align:center;width:100%">
        `).join('')}
      </div>
      <div id="will-msg" style="min-height:1.5em;margin-bottom:0.5rem"></div>
      <button class="btn btn-primary" id="willGoBtn" style="width:100%">Decrypt</button>
      <details style="margin-top:16px;text-align:left">
        <summary style="font-size:13px;color:var(--text-dim);cursor:pointer">Have a will access link instead?</summary>
        <div style="margin-top:8px">
          <input type="text" id="willInput" placeholder="Will access link or token" autocomplete="off"
            style="width:100%;padding:10px 12px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:14px;margin-bottom:8px">
          <button class="btn btn-secondary btn-sm" id="legacyWillBtn" style="width:100%">Continue</button>
        </div>
      </details>
    </div>`;

  const inputs = app.querySelectorAll('.mnemonic-word');
  inputs[0].focus();

  inputs.forEach((inp, idx) => {
    inp.addEventListener('paste', (e) => {
      const text = (e.clipboardData || window.clipboardData).getData('text').trim();
      const pastedWords = text.split(/\s+/);
      if (pastedWords.length === 12) {
        e.preventDefault();
        pastedWords.forEach((w, i) => { if (inputs[i]) inputs[i].value = w.toLowerCase(); });
        inputs[11].focus();
      }
    });
    inp.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Tab') {
        if (e.key === ' ') e.preventDefault();
        if (idx < 11) inputs[idx + 1].focus();
      }
      if (e.key === 'Backspace' && !inp.value && idx > 0) {
        inputs[idx - 1].focus();
      }
      if (e.key === 'Enter') {
        document.getElementById('willGoBtn').click();
      }
    });
  });

  document.getElementById('willGoBtn').onclick = async () => {
    const btn = document.getElementById('willGoBtn');
    const msg = document.getElementById('will-msg');
    const mnemonic = Array.from(inputs).map(i => i.value.trim().toLowerCase()).join(' ');

    if (mnemonic.split(/\s+/).filter(Boolean).length !== 12) {
      msg.innerHTML = '<span style="color:var(--danger)">Please enter all 12 words.</span>';
      return;
    }

    const valid = await validateBip39Mnemonic(mnemonic);
    if (!valid) {
      msg.innerHTML = '<span style="color:var(--danger)">Invalid mnemonic. Check your words and try again.</span>';
      return;
    }

    btn.disabled = true;
    msg.innerHTML = '<span class="loading">Looking up will...</span>';

    try {
      const lookupKey = await deriveWillLookupKey(mnemonic);
      const data = await get(`/will/access/by-words/${lookupKey}`);

      msg.innerHTML = '<span class="loading">Deriving key...</span>';
      const wrappingKey = await deriveWillWrappingKey(mnemonic);
      const encryptedWillKey = new Uint8Array(data.encrypted_will_key);
      const willKey = unwrapWillKey(wrappingKey, encryptedWillKey);
      wipe(wrappingKey);

      msg.innerHTML = '<span class="loading">Decrypting items...</span>';
      const decryptedItems = decryptAllItems(data, willKey);
      renderDecryptedView(app, decryptedItems, null, willKey, lookupKey);
    } catch (err) {
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        msg.innerHTML = '<span style="color:var(--danger)">Will not found. It may have expired or the words are incorrect.</span>';
      } else {
        msg.innerHTML = `<span style="color:var(--danger)">${esc(err.message)}</span>`;
      }
      btn.disabled = false;
    }
  };

  // Legacy link/UUID fallback
  document.getElementById('legacyWillBtn').onclick = legacyGo;
  const legacyInput = document.getElementById('willInput');
  legacyInput.onkeydown = (e) => { if (e.key === 'Enter') legacyGo(); };

  function legacyGo() {
    const val = document.getElementById('willInput').value.trim();
    const slugMatch = val.match(/[#/]*will\/((?:[a-z]+-){11}[a-z]+)/i);
    if (slugMatch) { window.location.hash = `#/will/${slugMatch[1].toLowerCase()}`; return; }
    const tokenMatch = val.match(/[#/]*will\/([0-9a-f-]{36})/i);
    if (tokenMatch) { window.location.hash = `#/will/${tokenMatch[1]}`; return; }
    const uuidMatch = val.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    if (uuidMatch) { window.location.hash = `#/will/${val}`; return; }
    document.getElementById('will-msg').innerHTML = '<span style="color:var(--danger)">Enter a valid will access link or token.</span>';
  }
}

function decryptAllItems(data, willKey) {
  const wrappedMap = {};
  if (Array.isArray(data.wrapped_items)) {
    data.wrapped_items.forEach(wi => { wrappedMap[wi.item_id] = wi; });
  }

  const decryptedItems = [];
  for (const item of data.items) {
    const wrappedEntry = wrappedMap[item.item_id];
    if (!wrappedEntry) continue;

    try {
      if (item.encrypted_blob) {
        const plaintext = decryptWillItem(willKey, wrappedEntry, item.encrypted_blob);
        let parsed = null;
        try { parsed = JSON.parse(plaintext); } catch (_) {}

        if (parsed && parsed.filename) {
          decryptedItems.push({
            item_id: item.item_id,
            item_type: parsed.item_type || 'file',
            name: parsed.name || parsed.filename || 'Untitled',
            content_type: parsed.mime_type || 'application/octet-stream',
            file_blob_key: item.file_blob_key,
            wrappedEntry,
            is_file: true,
          });
        } else if (parsed && parsed.content !== undefined) {
          decryptedItems.push({
            item_id: item.item_id,
            item_type: parsed.item_type || 'unknown',
            name: parsed.name || 'Untitled',
            plaintext: parsed.content,
            is_file: false,
          });
        } else {
          decryptedItems.push({
            item_id: item.item_id,
            item_type: 'unknown',
            name: 'Untitled',
            plaintext,
            is_file: false,
          });
        }
      }
    } catch (e) {
      decryptedItems.push({
        item_id: item.item_id,
        item_type: 'unknown',
        name: 'Item',
        error: e.message,
        is_file: false,
      });
    }
  }
  return decryptedItems;
}

function renderDecryptedView(app, items, token, willKey, lookupKey) {
  // For blob fetches, use token if available, otherwise use by-words endpoint
  const blobUrlBase = token
    ? `/will/access/${token}/blob`
    : `/will/access/by-words/${lookupKey}/blob`;

  app.innerHTML = `
    <h1>Digital Heir Access</h1>
    <div class="card">
      <p style="color:var(--text-dim);margin-bottom:1rem">
        You have been granted access as a digital heir. The items below have been decrypted.
      </p>
      <div class="detail-row"><span class="detail-label">Items Available</span><span>${items.length}</span></div>
    </div>

    <div class="section" style="margin-top:1rem">
      <h2>Items</h2>
      ${items.length ? `
        <table>
          <thead><tr><th>Type</th><th>Name</th><th></th></tr></thead>
          <tbody>
            ${items.map((item, i) => `<tr>
              <td>${esc(item.item_type)}</td>
              <td>${esc(item.name)}${item.error ? ' <span style="color:var(--danger)">(decrypt error)</span>' : ''}</td>
              <td>
                ${item.error ? '' :
                  item.is_file ?
                    `<button class="btn btn-primary btn-sm download-file-btn" data-index="${i}">Download File</button>` :
                    `<button class="btn btn-primary btn-sm view-btn" data-index="${i}">View</button>
                     <button class="btn btn-sm download-btn" data-index="${i}">Download</button>`
                }
              </td>
            </tr>`).join('')}
          </tbody>
        </table>
      ` : '<div class="empty">No items available</div>'}
    </div>

    <div id="item-viewer" style="display:none;margin-top:1rem" class="card">
      <h3 id="viewer-title"></h3>
      <pre id="viewer-content" style="white-space:pre-wrap;word-break:break-word;max-height:400px;overflow:auto"></pre>
      <button class="btn btn-sm" id="viewer-close">Close</button>
    </div>
  `;

  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.onclick = () => {
      const item = items[parseInt(btn.dataset.index)];
      document.getElementById('viewer-title').textContent = item.name;
      document.getElementById('viewer-content').textContent = item.plaintext;
      document.getElementById('item-viewer').style.display = '';
    };
  });

  document.getElementById('viewer-close')?.addEventListener('click', () => {
    document.getElementById('item-viewer').style.display = 'none';
  });

  document.querySelectorAll('.download-btn').forEach(btn => {
    btn.onclick = () => {
      const item = items[parseInt(btn.dataset.index)];
      const blob = new Blob([item.plaintext], { type: 'text/plain' });
      downloadBlob(blob, `${item.name}.txt`);
    };
  });

  document.querySelectorAll('.download-file-btn').forEach(btn => {
    btn.onclick = async () => {
      const item = items[parseInt(btn.dataset.index)];
      btn.disabled = true;
      btn.textContent = 'Downloading...';
      try {
        const ab = await getRaw(`${blobUrlBase}/${item.item_id}`);
        const encryptedData = new Uint8Array(ab);
        const decrypted = decryptWillFileBlob(willKey, item.wrappedEntry, encryptedData);
        const blob = new Blob([decrypted], { type: item.content_type });
        downloadBlob(blob, item.name);
      } catch (e) {
        toast(`Download failed: ${e.message}`);
      } finally {
        btn.disabled = false;
        btn.textContent = 'Download File';
      }
    };
  });
}

function renderLockedView(app, data, token) {
  const hasHeirKeys = data.ephemeral_pubkey && data.ephemeral_pubkey.length > 1;

  app.innerHTML = `
    <h1>Digital Heir Access</h1>
    <div class="card">
      <div class="msg msg-error">
        ${hasHeirKeys ?
          'Unable to decrypt. Please log in with the heir account to decrypt these items.' :
          'Unable to decrypt. Enter the passphrase at the will access page.'}
      </div>
      <div class="detail-row"><span class="detail-label">Items Available</span><span>${data.items.length}</span></div>
      ${hasHeirKeys ? '<p style="margin-top:1rem"><a href="#/login" class="btn btn-primary">Log In</a></p>' :
        '<p style="margin-top:1rem"><a href="#/will" class="btn btn-primary">Enter Passphrase</a></p>'}
    </div>
  `;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
